import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, queueScheduler  } from 'rxjs';
import { mergeMap, observeOn, tap } from 'rxjs/operators';
import { AddPhoto } from '../apimodels/photo/add-photo';
import { PhotoUrls } from '../apimodels/photo/photo-urls';
import { TransformPhoto } from '../apimodels/photo/transform-photo';
import { TransformedPhoto } from '../apimodels/photo/transformed-photo';
import { Environment, ENVIRONMENT } from '@classifieds-sell/environment';

interface UploadQueueItem {
    photo: AddPhoto;
    resultSubject: Subject<PhotoUrls>;
  }

  @Injectable({
    providedIn: 'root'
})
export class PhotoService implements OnDestroy{
    private _uploadQueue = new Subject<UploadQueueItem>();
    private _maxConcurrentUploads = 1;
    private _uploadSubscription: Subscription;

    constructor(
      private httpClient: HttpClient,
      @Inject(ENVIRONMENT) private env: Environment
    ) {
        this._uploadSubscription = this._uploadQueue
          .pipe(
            observeOn(queueScheduler),
            mergeMap(item =>
              this.makeUploadPhotoRequest(item.photo)
              .pipe(
                // When the HTTP request completes:
                tap((photoUrls: PhotoUrls) => {
                  // Emit the photoUrls through the correct resultSubject
                  item.resultSubject.next(photoUrls);
                  // Signal completion to the resultSubject
                  item.resultSubject.complete();
                })
              ),
              this._maxConcurrentUploads
            )
          )
          .subscribe();
      }

    ngOnDestroy(): void {
      if (this._uploadSubscription){
        this._uploadSubscription.unsubscribe();
      }
    }

    uploadPhoto(photo: AddPhoto): Observable<PhotoUrls> {
        return this.makeUploadPhotoRequest(photo);
    }

    queuePhotoForUpload(photo: AddPhoto): Observable<PhotoUrls> {
        const resultSubject = new Subject<PhotoUrls>();

        this._uploadQueue.next({photo, resultSubject});

        return resultSubject.asObservable();
    }

    private makeUploadPhotoRequest(photo: AddPhoto): Observable<PhotoUrls> {
      return this.httpClient.post<PhotoUrls>(
          `${this.env.apiUrl}/Photos`,
          photo,
          {
              withCredentials: true
          }
      );
    }

    transformPhoto(photo: TransformPhoto): Observable<TransformedPhoto> {
        return this.httpClient.post<PhotoUrls>(
            `${this.env.apiUrl}/Photos/Transform`,
            photo,
            {
                withCredentials: true
            }
        );
    }

    uploadAgentPhoto(photo: AddPhoto): Observable<PhotoUrls> {
        return this.httpClient.post<PhotoUrls>(
            `${this.env.apiUrl}/Photos/Agent`,
            photo,
            {
                withCredentials: true
            }
        );
    }
}
